import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { HttpResponse } from '@angular/common/http';
import { combineLatest } from 'rxjs';

import { environment } from '@environment/environment';

import { TitleService } from '@core/services/title.service';

import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';

import { ProjectsRequests } from '@app/store/state/projects/projects.requests';
import { VersionsRequests } from '@app/store/state/versions/versions.requests';
import { AppService } from '@app/services/app/app.service';

import { ProjectRelatedVersionsService } from '@core/services/project-related-versions/project-related-versions.service';

class SelectedFilesToCompare {
    public target: Objects.AmsFile = null;
    public reference: Objects.AmsFile = null;
    public selected: Array<Objects.AmsFile> = [];
    public completed: boolean = false;

    isComplete(): boolean {
        return this.selected.length == 2;
    }

    changeOrder() {
        let tmp = this.target;
        this.target = this.reference;
        this.reference = tmp;
    }

    setSelected(files: Array<Objects.AmsFile>) {
        this.selected = files;

        this.applyChanges();
    }

    applyChanges() {
        this.removeIfNotExist('reference');
        this.removeIfNotExist('target');

        this.setIfExist('reference');
        this.setIfExist('target');

        this.completed = this.isComplete();
    }

    remove(type: string) {
        let file = this.get(type);
        let files = this.selected.filter(f => f.id != file.id);
        this.setSelected(files);
    }

    protected has(type: string): boolean {
        return this[type] != null;
    }

    protected get(type: string): Objects.AmsFile | null {
        return this[type];
    }

    protected removeIfNotExist(type: string ) {
        if(this.has(type)) {
            let file = this.get(type);
            let files = this.selected.filter(f => f.id == file.id);
            if(files.length == 0)
                this[type] = null;
        }
    }

    protected setIfExist(type: string) {
        let opposite = type == 'reference'? 'target' : 'reference';
        if(!this.has(type)) {
            let files = [...this.selected];
            if(this.has(opposite)) {
                let remove = this.get(opposite);
                files = files.filter(f => f.id != remove.id);
            }

            if(files.length > 0)
                this[type] = files[0];

        }
    }

}

@Component({
    selector: 'bridges-filecomparison-project-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class BridgesFileComparisonProjectMainComponent implements OnInit, OnDestroy {

    public readonly LOADING = 0;
    public readonly DONE = 1;
    public readonly NOT_FOUND = 3;

    protected readonly PLUGIN_REQUEST_FEEDBACK = 'share_feedback';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';

    readonly fileComparisonBaseUri: string = environment.fileComparisonBaseUri;

    public reference: Objects.AmsFile = null;
    public target: Objects.AmsFile = null;

    public status:number = this.LOADING;
    public project: Objects.Request = null;
    public partialProject: Objects.BasicRequest = null;
    public filesUrl: string = "";

    public files: SelectedFilesToCompare = new SelectedFilesToCompare();

    public breadcrumbText: string = 'SELECT FILES TO COMPARE';
    public breadcrumbs: any = [
        {
            title: "",
            link: ['']
        }
    ];

    constructor(
        private projectsReqSrv: ProjectsRequests,
        private versionsReqSrv: VersionsRequests,
        private appSrv: AppService,
        private projectRVSrv: ProjectRelatedVersionsService,
        private activeRoute: ActivatedRoute,
        private title: TitleService){}

    ngOnInit() {
        let projectId = this.activeRoute.snapshot.paramMap.get('pid');
        let artworkId = this.activeRoute.snapshot.paramMap.get('aid');
        let fileUuid = this.activeRoute.snapshot.paramMap.get('fuuid');

        this.title.set("Select files to compare from Project #" +projectId+" - XRay");
        this.filesUrl = "/projects/" + projectId +"/attachments";

        // access is checked here (instead of a route guard) so a denied user sees the
        // inline 404 box on this same URL rather than being redirected to /error/not-found.
        combineLatest([this.appSrv.getSession(), this.appSrv.getAccountSession()]).subscribe(([session, accountSession]: [any, AccountSession]) => {
            // apps list now comes from the accounts-api session; fall back to the legacy session field
            // in case the (separate, parallel) accounts-api call hasn't resolved yet
            let apps = accountSession?.current?.apps ?? session?.options?.organization?.apps;
            let appRegistered = apps?.some((a: string) => a == this.APP_FILECOMPARISON) || false;
            let accessGlobalXray = session?.options?.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
            if (!(accessGlobalXray && appRegistered)) {
                this.status = this.NOT_FOUND;
                return;
            }

            // enables the related-versions feedback status batch call in core-versions-related-selector
            let feedbackAccess = session?.options?.plugins?.some((p: string) => p == this.PLUGIN_REQUEST_FEEDBACK) || false;
            this.projectRVSrv.initialice(feedbackAccess);

            this.loadRequest(Number(projectId));
            if(fileUuid)
                this.preloadReferenceFile(fileUuid, artworkId ? Number(artworkId) : null, Number(projectId));
        });
    }

    onChange(e) {
        if(e.length<3)
            this.files.setSelected(e);
    }

    ngOnDestroy(){

    }

    cancel() {
        this.files.remove('reference');
        this.files.remove('target');
    }

    goToCompare() {
        if(!this.files.isComplete())
            return;

        // navigate to the external file comparison app, passing the AmsFile uuids
        let query = new URLSearchParams({
            a: this.files.reference.uuid,
            b: this.files.target.uuid
        });

        window.open(this.fileComparisonBaseUri + "/compare?" + query.toString(), "_blank");

    }


    protected preloadReferenceFile(fileUuid: string, artworkId: number | null, projectId: number) {
        let files$ = artworkId ? this.versionsReqSrv.loadAssets$(artworkId) : this.projectsReqSrv.loadFiles$(projectId);

        files$.subscribe({
            next: (files: Array<Objects.AmsFile>) => {
                let file = files.find((f: Objects.AmsFile) => f.uuid == fileUuid);
                if(file)
                    this.files.setSelected([file]);
            },
            error: (error: any) => {}
        });
    }

    protected loadRequest(projectId: number){
        this.projectsReqSrv.load$(projectId).subscribe({
                    next: (response: HttpResponse<Objects.Request>) => {
                        if (response.status === 200) {
                            // Full success
                            this.project = response.body as Objects.Request;
                        } else if (response.status === 206) {
                            // Partial content: User has limited access, set project partial instead
                            this.partialProject = response.body as Objects.BasicRequest;
                        }
                        this.updateBreadcrumbs(response.body as Objects.Request);
                        this.status = this.DONE;
                    },
                    error: (error:any) => {
                        this.status = this.NOT_FOUND;
                    }
                });
    }

    protected updateBreadcrumbs(project: Objects.Request) {
        this.breadcrumbs[0].link = '/projects/' + project.id;
        this.breadcrumbs[0].title = 'Project <small>(#' + project.id + ')</small>';
    }
}
